import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { useOidcProfile, useUserManager } from "../../services/auth";
import Header from "../../components/Header";
import Navbar, { Button } from "../../components/Navbar";
import { ISidebarNavItem } from "../../components/Navigation/types";
import BaseLayout from "../BaseLayout";
import Sidebar from "../Sidebar";
import TenantSelect from "./TenantSelect";
import { getNavItems } from "./config";
import { useAbility } from "../../hooks";
import { selectPaymentFailureStanding } from "../../store/standing";
import PaymentBanner from "./PaymentBanner";
import MenuToggle from "./MenuToggle";
import Username from "./Username";
import { supportLinks } from "./PaymentBanner/config";
import {
  fetchProductLinesRequest,
  selectBrandCode,
} from "../../store/metadata";
import { selectCustomerData, selectPrincipalData } from "../../store/user";
import RemoveLicenseBanner from "./RemoveLicenseBanner";
import {
  selectCurrentCustomerSubscription,
  selectCurrentSubscriptionDetail,
  selectFeatureLicenses,
  selectIsShouldUnassignedLicences,
} from "../../store/subscriptions";
import { selectAssignedLicenses } from "../../store/manageUsers";
import { getSubscriptionStatus } from "../../utils/common";
import { SP_STATES } from "../../services/api/subscription/constants";
import { isPhSubscriptionManaged } from "../../constants/spCode";
import { HeaderLoader } from "../../components/Loader/HeaderLoader/HeaderLoader";
import { SUBSCRIPTION_STATUS } from "../../utils/common/contants";
import { subscriptions } from "../../config/router/routes";

import "./styles.scss";

export const PrivateLayout: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userManager = useUserManager();
  const ability = useAbility();
  const { pathname } = useLocation();
  const paymentFailureStanding = useSelector(selectPaymentFailureStanding);
  const [supportLink, setSupportLink] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShowHeaderLoader, setIsShowHeaderLoader] = useState(true);
  const [isShowSidebarLoader, setIsShowSidebarLoader] = useState(true);
  const userData = useSelector(selectPrincipalData);
  const customer = useSelector(selectCustomerData);
  const profile = useOidcProfile();
  const currentSubscription = useSelector(selectCurrentCustomerSubscription);
  const brand = useSelector(selectBrandCode);
  const isAdmin = brand === "ADMIN";
  const subscriptionDetails = useSelector(selectCurrentSubscriptionDetail);
  const shouldRemoveLicence = useSelector(selectIsShouldUnassignedLicences);
  const featureLicenses = useSelector(selectFeatureLicenses);
  const assignedLicenses = useSelector(selectAssignedLicenses);

  // Determine sp_code of currently selected past-due subscription if any.
  const pastDueSpCode = useMemo(() => {
    if (!subscriptionDetails) {
      return undefined;
    }

    if (
      paymentFailureStanding ||
      subscriptionDetails.sp?.states?.includes(SP_STATES.PAST_DUE) ||
      getSubscriptionStatus(subscriptionDetails) ===
        SUBSCRIPTION_STATUS.paymentFailure
    ) {
      return subscriptionDetails.sp?.sp_code;
    }

    return undefined;
  }, [paymentFailureStanding, subscriptionDetails]);

  // Check if past-due subscription requires renewal invoice vs. payment details.
  const isPhSubscriptionPastDue = isPhSubscriptionManaged(pastDueSpCode);

  // ...and whether that subscription is still being SERVED, which decides which
  // of the two things the banner can truthfully say. `past_due` is a grace
  // period for a PH_SUBSCRIPTION: ph-api reports it `is_active: true` and the
  // VPN stays unlocked until the grace runs out, so the message is "overdue",
  // not "paused". The banner is reachable for a genuinely stopped one too -
  // an inactive row (a provider that reports past due as inactive), or a
  // PaymentFailure standing left behind on a subscription that has since
  // expired, since standings are only cleared while the subscription is active
  // - and that one gets the stopped wording instead.
  const isPhSubscriptionServed =
    isPhSubscriptionPastDue &&
    !!subscriptionDetails?.sp?.is_active &&
    !subscriptionDetails?.isTerminated;

  const isShowPaymentBanner = useMemo(() => {
    // Hide banner on subscriptions page; card already shows renewal banner.
    if (isPhSubscriptionPastDue && pathname === subscriptions.path) {
      return false;
    }

    if (paymentFailureStanding) {
      return true;
    }

    if (subscriptionDetails) {
      const status = getSubscriptionStatus(subscriptionDetails);
      return (
        (status === SUBSCRIPTION_STATUS.active &&
          subscriptionDetails.sp.states.includes(SP_STATES.PAST_DUE)) ||
        status === SUBSCRIPTION_STATUS.paymentFailure
      );
    }

    return false;
  }, [
    paymentFailureStanding,
    subscriptionDetails,
    isPhSubscriptionPastDue,
    pathname,
  ]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (customer) {
      dispatch(fetchProductLinesRequest());
    }
  }, [dispatch, customer]);

  const handleSignout = () => {
    userManager.signoutRedirect();
  };

  const toggleMenu = () => {
    setIsMenuOpen((state) => {
      return !state;
    });
  };

  useEffect(() => {
    if (userData) {
      setSupportLink(supportLinks[userData.tenant_code]);
    }
  }, [userData]);

  useEffect(() => {
    if ((userData && profile && brand) || isAdmin) {
      setIsShowHeaderLoader(false);
    }
  }, [userData, profile, brand, isAdmin]);

  useEffect(() => {
    if ((ability && customer && brand) || isAdmin) {
      setIsShowSidebarLoader(false);
    }
  }, [ability, customer, brand, currentSubscription, isAdmin]);

  const navItems = getNavItems().filter((navItem) => {
    if (!navItem.route) {
      return true;
    }

    return navItem.route.isRedirectAllowed({
      ability,
      customer,
      brandCode: brand,
      currentSubscription: currentSubscription,
    });
  });
  const mobileNavItems: ISidebarNavItem[] = [
    ...navItems,
    {
      label: "logout",
      icon: "right",
      onClick: handleSignout,
    },
  ];

  return (
    <BaseLayout className="ams-layout--private">
      {shouldRemoveLicence && subscriptionDetails?.sp?.next_invoices_at ? (
        <RemoveLicenseBanner
          date={subscriptionDetails?.sp?.next_invoices_at}
          assignedLicenses={assignedLicenses}
          featureLicenses={featureLicenses}
        />
      ) : null}

      {isShowPaymentBanner && (
        <PaymentBanner
          isPhSubscription={isPhSubscriptionPastDue}
          isServiceActive={isPhSubscriptionServed}
        />
      )}
      {isShowHeaderLoader ? (
        <HeaderLoader />
      ) : (
        <Header>
          <>
            {isAdmin ? <TenantSelect /> : null}
            <Navbar className="ams-header__navbar">
              <Username username={profile?.preferred_username} />
              {!isAdmin ? (
                <a
                  className="navbar__link ams-header__navbar-item"
                  href={supportLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("help")}
                </a>
              ) : null}
              <Button
                className="ams-header__navbar-item"
                onClick={handleSignout}
              >
                {t("logout")}
              </Button>
            </Navbar>
            <MenuToggle
              className="ams-header__menu-toggle"
              onClick={toggleMenu}
              isToggled={isMenuOpen}
            />
            {isMenuOpen && (
              <div className="ams-header__mobile-menu">
                <Sidebar
                  navItems={mobileNavItems}
                  className="ams-header__menu"
                  isShowSidebarLoader={isShowSidebarLoader}
                />
                <Navbar className="ams-header__mobile-navbar">
                  <Username username={profile?.preferred_username} />
                  {!isAdmin ? (
                    <a
                      className="navbar__link ams-header__navbar-item"
                      href={supportLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("help")}
                    </a>
                  ) : null}
                </Navbar>
              </div>
            )}
          </>
        </Header>
      )}
      <div className="ams-layout__container">
        <Sidebar
          className="ams-layout__sidebar"
          navItems={navItems}
          isShowSidebarLoader={isShowSidebarLoader}
        />
        <main className="ams-layout__content">{children}</main>
      </div>
    </BaseLayout>
  );
};
